/**
 * ProjectNotesButton — the shared "Project Notes (N)" control (BO UI Standard §8).
 *
 * §8 makes Project Notes mandatory on every BO screen, yet only ~8 of the app's
 * ~470 navigable forms have it, because the popup that implements it
 * (`NotesListPopup`) lives in `features/analysis/SdmsProjectNotes.tsx` under an
 * SDMS-sounding name — so every other module reads it as "the SDMS one" and
 * writes nothing. It is in fact fully general: it already accepts a caller-owned
 * `baseUrl` (Field Data Entry uses that today) and a `businessObject` caption.
 *
 * This component is the neutral front door for it: a button that shows the live
 * count and opens the popup, for ANY form that exposes the four standard routes
 *
 *     GET|POST    {baseUrl}
 *     PUT|DELETE  {baseUrl}/{noteOid}
 *
 * over the shared `dbo.Notes`. Nothing is re-implemented (rule 8) — the popup,
 * its author-only gate (§14a Rule C) and its count hook are the existing ones.
 *
 * ⚠️ The popup itself still LIVES in features/analysis. Moving it to
 * `components/ProjectNotes/` is the right end state but renames an export that 13
 * feature files import, so it is its own reviewed change; this wrapper is what
 * lets new forms adopt it correctly in the meantime.
 */
import { useState } from 'react';
import { NotesListPopup, useNotesCount } from '../../features/analysis/SdmsProjectNotes';

export interface ProjectNotesButtonProps {
  /** The owning record's Oid. When absent the button is disabled — a note has
   *  nothing to attach to until the record exists (§8's "gate on the record id"). */
  recordId?: string | null;
  /** The four note routes' base, e.g. `/api/v1/documents/{oid}/project-notes`.
   *  Required: this is what makes the shared popup form-agnostic. */
  baseUrl?: string | null;
  /** The record's human label, shown in the popup title (e.g. "DOC1005"). */
  recordLabel?: string;
  /** A sub-caption under the title (e.g. the document's name). */
  recordSubLabel?: string;
  /** The creating form's caption — a new note's Business Object, exactly as
   *  ALPACA saves View.Caption (e.g. "Document Creator"). */
  businessObject: string;
  /** View-only (no New / Delete, editor read-only) — for a read-only screen. */
  readOnly?: boolean;
  /** Why the button is disabled, shown as its tooltip. Defaults to the §8 wording. */
  disabledReason?: string;
  className?: string;
}

export function ProjectNotesButton({
  recordId,
  baseUrl,
  recordLabel = '',
  recordSubLabel = '',
  businessObject,
  readOnly = false,
  disabledReason = 'Save this record before adding Project Notes',
  className = 'ca-btn',
}: ProjectNotesButtonProps) {
  const [open, setOpen] = useState(false);
  const enabled = !!recordId && !!baseUrl;
  // The hook needs a stable arg set; with no record it is given none and returns 0.
  const count = useNotesCount('', null, enabled ? baseUrl! : undefined);

  return (
    <>
      <button
        type="button"
        className={className}
        disabled={!enabled}
        title={enabled ? `Project Notes for ${recordLabel || 'this record'}` : disabledReason}
        onClick={() => setOpen(true)}
      >
        {/* The `.tb-i` emoji vocabulary, NOT a Tabler class: every `ti-*` glyph in
          this app resolves to an EMPTY ::before (verified in-browser on ti-notes,
          ti-clock and ti-chevron-right alike), so `ti ti-notes` drew a blank box
          next to neighbours that render fine — Refresh 🔄, Export 📤, Audit 🛡️.
          (User report 2026-10-12: "project notes button icon is wrong".) */}
        <span className="tb-i tb-i-blue">📝</span> Project Notes{count > 0 ? ` (${count})` : ''}
      </button>

      {open && enabled && (
        <NotesListPopup
          batchId={recordId!}
          batchTest={recordSubLabel}
          batchLabel={recordLabel}
          businessObject={businessObject}
          baseUrl={baseUrl!}
          readOnly={readOnly}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export default ProjectNotesButton;
